import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatEURCompact, daysUntil } from "@/lib/api";
import { toast } from "sonner";
import { Warning, ArrowRight, ArrowClockwise, Check } from "@phosphor-icons/react";

/**
 * Page Relances en retard — espace de traitement opérationnel dédié.
 * Tri métier prioritaire : Banque > À envoyer > Montage > Apporteurs > Clients.
 * 2 niveaux visuels : léger (1-3j) vs critique (7+j) — pas de mer de rouge.
 */
const PRIORITY_ORDER = ["banque", "envoyer", "montage", "apporteurs", "clients"];
const PRIORITY_LABEL = {
  banque: "Banque",
  envoyer: "À envoyer",
  montage: "Montage",
  apporteurs: "Apporteurs",
  clients: "Clients",
};

function severityFor(daysLate) {
  // daysLate > 0 (jours en retard)
  if (daysLate >= 7) return "critique";
  return "leger";
}

function categoryForDossier(d) {
  if (d.status === "signe") return "clients";
  if (!d.mandat_signe) return "montage";
  if (!d.date_envoi_banque) return "envoyer";
  return "banque";
}

function RelanceCard({ item, onMarked }) {
  const navigate = useNavigate();
  const daysLate = -(item.daysUntil ?? 0); // positive int
  const sev = severityFor(daysLate);
  const bgClass = sev === "critique" ? "border-rose-300 bg-rose-50" : "border-orange-200 bg-orange-50/60";
  const dotColor = sev === "critique" ? "var(--cf-danger-text)" : "var(--cf-warning)";

  const handleMark = async (e) => {
    e.stopPropagation();
    try {
      if (item.kind === "dossier") {
        await api.post(`/dossiers/${item.id}/relance`);
      } else {
        await api.post(`/apporteurs/${encodeURIComponent(item.name)}/mark-contacted`);
      }
      toast.success("Marqué relancé");
      onMarked?.();
    } catch {
      toast.error("Erreur");
    }
  };

  const navigateTo = () => {
    if (item.kind === "dossier") navigate(`/dossier/${item.id}`);
    else navigate("/apporteurs");
  };

  return (
    <button
      type="button"
      onClick={navigateTo}
      className={`w-full text-left border rounded-xl px-3 py-3 flex items-center gap-3 hover:shadow-sm transition-all ${bgClass}`}
      data-testid={`retard-card-${item.uid}`}
    >
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ background: dotColor }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[14px] text-slate-900 truncate">{item.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
          {item.subtitle}
          <span className="ml-1.5 font-bold" style={{ color: dotColor }}>
            · en retard {daysLate}j
          </span>
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-1">
        <button
          type="button"
          onClick={handleMark}
          className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-emerald-400 hover:bg-emerald-50"
          title="Marquer relancé"
          data-testid={`retard-mark-${item.uid}`}
        >
          <Check size={13} weight="bold" color="var(--cf-success-text)" />
        </button>
        <ArrowRight size={13} weight="bold" className="text-slate-400" />
      </div>
    </button>
  );
}

export default function RelancesRetard() {
  const [dossiers, setDossiers] = useState([]);
  const [apporteurs, setApporteurs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [doss, app] = await Promise.all([
        api.get("/dossiers"),
        api.get("/apporteurs/stats"),
      ]);
      setDossiers(doss.data || []);
      setApporteurs((app.data?.list || []).filter((a) => a.late));
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const items = useMemo(() => {
    const out = [];
    for (const d of dossiers) {
      if (d.archived) continue;
      if (d.is_standby) continue;
      if (d.status === "perdu") continue;
      const rel = d.next_relance_date;
      if (!rel) continue;
      const dn = daysUntil(rel);
      if (dn === null || dn >= 0) continue; // pas en retard
      const cat = categoryForDossier(d);
      out.push({
        uid: `dossier-${d.id}`,
        kind: "dossier",
        id: d.id,
        category: cat,
        title: d.nom,
        subtitle: cat === "clients" ? "Suivi client" : `${PRIORITY_LABEL[cat]} · ${d.apporteur ? d.apporteur : "—"}`,
        daysUntil: dn,
      });
    }
    for (const a of apporteurs) {
      out.push({
        uid: `apporteur-${a.name}`,
        kind: "apporteur",
        name: a.name,
        category: "apporteurs",
        title: a.name,
        subtitle: `Apporteur Tier ${a.tier}`,
        daysUntil: a.days_until_next,
      });
    }
    // Trie : priorité métier d'abord, puis "le plus en retard"
    out.sort((x, y) => {
      const px = PRIORITY_ORDER.indexOf(x.category);
      const py = PRIORITY_ORDER.indexOf(y.category);
      if (px !== py) return px - py;
      return x.daysUntil - y.daysUntil; // plus négatif d'abord = plus en retard
    });
    return out;
  }, [dossiers, apporteurs]);

  // Groupage par catégorie
  const grouped = useMemo(() => {
    const g = {};
    for (const item of items) {
      g[item.category] = g[item.category] || [];
      g[item.category].push(item);
    }
    return g;
  }, [items]);

  return (
    <Layout
      title="Relances en retard"
      subtitle={
        <span className="flex items-center gap-1.5">
          <Warning size={12} weight="fill" color="var(--cf-danger)" /> Traitement prioritaire
        </span>
      }
    >
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-white border border-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card-base p-8 text-center" data-testid="retard-empty">
          <div className="text-5xl mb-3">✅</div>
          <h2 className="font-display text-xl font-extrabold text-slate-900 mb-1">Aucune relance en retard</h2>
          <p className="text-sm text-slate-500">Tout est à jour. Profite-en pour prospecter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-[12px] text-slate-500 font-semibold" data-testid="retard-count">
            {items.length} relance{items.length > 1 ? "s" : ""} à traiter
          </div>
          {PRIORITY_ORDER.map((cat) => {
            const list = grouped[cat];
            if (!list || list.length === 0) return null;
            return (
              <section key={cat} data-testid={`retard-section-${cat}`}>
                <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">
                  {PRIORITY_LABEL[cat]} · {list.length}
                </h3>
                <div className="space-y-2">
                  {list.map((item) => (
                    <RelanceCard key={item.uid} item={item} onMarked={fetchAll} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
